import { toBdPoint, readPointCoordinates } from '../geometry';
import { isBaiduMapGLReady } from '../loadBaiduMapGL';
import type { IMapEngine, MapOptions, LngLat, Pixel, Bounds, EventHandler } from '../types';

/**
 * 百度地图 GL 引擎适配器
 * 将 BMapGL SDK 的 API 统一到 IMapEngine 接口
 */
export class BaiduMapAdapter implements IMapEngine {
  private map: any = null;

  initSync(container: HTMLElement, options: MapOptions): void {
    if (!isBaiduMapGLReady()) {
      throw new Error('BMapGL SDK is not ready');
    }
    window.BMapGL.coordType = options.coordType ?? 'BMAP_COORD_GCJ02';
    this.map = new window.BMapGL.Map(container, {
      coordType: options.coordType ?? 'BMAP_COORD_GCJ02',
      zoomWithCenter: true,
      enableDblclickZoom: false,
      displayOptions: options.displayOptions ?? { building: false },
    });
    this.map.centerAndZoom(toBdPoint(options.center), options.zoom);
    if (options.enableScrollWheelZoom !== false) {
      this.map.enableScrollWheelZoom();
    }
    if (options.minZoom != null) this.map.setMinZoom(options.minZoom);
    if (options.maxZoom != null) this.map.setMaxZoom(options.maxZoom);
  }

  async init(container: HTMLElement, options: MapOptions): Promise<void> {
    this.initSync(container, options);
  }

  destroy(): void {
    this.map?.clearOverlays?.();
    this.map = null;
  }

  setCenter(center: LngLat): void {
    this.map?.panTo(toBdPoint(center));
  }

  getCenter(): LngLat {
    return readPointCoordinates(this.map?.getCenter());
  }

  setZoom(zoom: number): void {
    this.map?.setZoom(zoom);
  }

  getZoom(): number {
    return this.map?.getZoom() ?? 0;
  }

  setMinZoom(zoom: number): void {
    this.map?.setMinZoom(zoom);
  }

  setMaxZoom(zoom: number): void {
    this.map?.setMaxZoom(zoom);
  }

  fitBounds(bounds: Bounds): void {
    if (!this.map) return;
    const sw = toBdPoint(bounds[0]);
    const ne = toBdPoint(bounds[1]);
    const bds = new window.BMapGL.Bounds(sw, ne);
    this.map.setViewport([bds]);
  }

  panTo(center: LngLat): void {
    this.map?.panTo(toBdPoint(center));
  }

  checkResize(): void {
    this.map?.checkResize?.();
  }

  addOverlay(overlay: any): void {
    this.map?.addOverlay(overlay);
  }

  removeOverlay(overlay: any): void {
    this.map?.removeOverlay(overlay);
  }

  clearOverlays(): void {
    this.map?.clearOverlays?.();
  }

  on(event: string, handler: EventHandler): void {
    this.map?.addEventListener(event, handler);
  }

  off(event: string, handler: EventHandler): void {
    this.map?.removeEventListener(event, handler);
  }

  lngLatToPixel(lngLat: LngLat): Pixel {
    const point = toBdPoint(lngLat);
    const pixel = this.map?.pointToOverlayPixel(point);
    return { x: pixel?.x ?? 0, y: pixel?.y ?? 0 };
  }

  pixelToLngLat(pixel: Pixel): LngLat {
    if (!this.map) return [0, 0];
    const point = this.map.overlayPixelToPoint(
      new window.BMapGL.Pixel(pixel.x, pixel.y)
    );
    return readPointCoordinates(point);
  }

  pointToOverlayPixel(point: any): Pixel {
    const pixel = this.map?.pointToOverlayPixel(point);
    return { x: pixel?.x ?? 0, y: pixel?.y ?? 0 };
  }

  refineEventLngLat(roughLngLat: LngLat, pixel: Pixel): LngLat {
    const map = this.map;
    if (
      !map ||
      typeof map.lnglatToMercator !== 'function' ||
      typeof map.mercatorToLnglat !== 'function'
    ) {
      return roughLngLat;
    }

    let [lng, lat] = roughLngLat;
    // 迭代精化：把「投影像素 vs 事件像素」的残差按当前 zoom 的分辨率
    // （1px = 2^(18-zoom) 墨卡托单位）回灌修正，最多 3 轮收敛。
    // 残差本身由 pointToOverlayPixel（float64 精确路径）计算，收敛后
    // 经纬度与点击像素的误差 ≤0.5px，与 zoom 级别无关。
    for (let i = 0; i < 3; i++) {
      const projected = map.pointToOverlayPixel(new window.BMapGL.Point(lng, lat));
      const dx = pixel.x - projected.x;
      const dy = pixel.y - projected.y;
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) break;
      const scale = Math.pow(2, 18 - map.getZoom());
      const [mcLng, mcLat] = map.lnglatToMercator(lng, lat);
      // 墨卡托 Y 轴向北为正，像素 Y 轴向南为正
      [lng, lat] = map.mercatorToLnglat(mcLng + dx * scale, mcLat - dy * scale);
    }
    return [lng, lat];
  }

  getNativeMap(): any {
    return this.map;
  }

  getPanes(): any {
    return this.map?.getPanes();
  }
}
